import { useNavigate } from "react-router-dom";
import { FlowNav } from "../components/FlowNav";
import {
  resolveConcernContactNow,
  resolveConcernFromAnswers,
} from "../concern/resolveBystander";
import {
  resolveContactNow,
  resolveFromAnswers,
} from "../help/resolveIndividual";
import {
  resolveHospitalityContactNow,
  resolveHospitalityFromAnswers,
} from "../hospitality/resolveHospitality";
import { selectResourcesForCategories } from "../resources/selectResources";
import type { QuietViaResource } from "../resources/types";
import { useSession } from "../state/SessionProvider";

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  national_hotline: "resources.category.national_hotline",
  emergency_services: "resources.category.emergency_services",
  survivor_services: "resources.category.survivor_services",
  shelter: "resources.category.shelter",
  legal_aid: "resources.category.legal_aid",
  labor_rights: "resources.category.labor_rights",
  immigration_assistance: "resources.category.immigration_assistance",
  child_protection: "resources.category.child_protection",
  healthcare: "resources.category.healthcare",
  hospitality_protocol: "resources.category.hospitality_protocol",
};

function ResourceCard({
  resource,
  translate,
}: {
  resource: QuietViaResource;
  translate: (key: string) => string;
}) {
  const tel =
    resource.phone && resource.phone.length > 0
      ? `tel:${resource.phone}`
      : null;
  const web =
    resource.url && /^https:\/\//i.test(resource.url) ? resource.url : null;

  return (
    <li className="resource-card">
      <h3>{resource.displayName}</h3>
      <p>{resource.description}</p>
      {resource.availability ? (
        <p className="muted">{resource.availability}</p>
      ) : null}
      {resource.languagesNote ? (
        <p className="muted">{resource.languagesNote}</p>
      ) : null}
      {resource.smsDisplay ? (
        <p className="muted">
          {translate("resources.sms_label")}: {resource.smsDisplay}
          {resource.smsInstructions ? ` — ${resource.smsInstructions}` : ""}
        </p>
      ) : null}
      {resource.kind === "guidance" ? (
        <p className="muted">{translate("resources.guidance_note")}</p>
      ) : null}
      <div className="resource-actions">
        {tel ? (
          <a className="mode-link mode-link--quiet" href={tel}>
            {translate("resources.action.call")}
            {resource.phoneDisplay ? ` (${resource.phoneDisplay})` : ""}
          </a>
        ) : null}
        {web ? (
          <a
            className="mode-link mode-link--quiet"
            href={web}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translate("resources.action.visit")}
          </a>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Curated static resources — verified national set; local jurisdiction pending owner confirmation.
 * Categories stay in React memory only.
 */
export function ResourcesPage() {
  const navigate = useNavigate();
  const {
    translate,
    helpAnswers,
    concernAnswers,
    hospitalityAnswers,
    resourceCategories,
    setResourceCategories,
    resourceReturnPath,
    clearHelpAnswers,
    clearConcernAnswers,
    clearHospitalityAnswers,
    setHelpBookmark,
    setConcernBookmark,
    setHospitalityBookmark,
    qrContext,
    clearQrContext,
  } = useSession();

  const fromConcern = resourceReturnPath === "/concern";
  const fromHospitality = resourceReturnPath === "/pro/hospitality";
  const backTo = resourceReturnPath || "/";

  const fallbackCategories = fromHospitality
    ? resolveHospitalityFromAnswers(hospitalityAnswers).resourceCategories
        .length > 0
      ? resolveHospitalityFromAnswers(hospitalityAnswers).resourceCategories
      : resolveHospitalityContactNow().resourceCategories
    : fromConcern
      ? resolveConcernFromAnswers(concernAnswers).resourceCategories.length > 0
        ? resolveConcernFromAnswers(concernAnswers).resourceCategories
        : resolveConcernContactNow().resourceCategories
      : resolveFromAnswers(helpAnswers).resourceCategories.length > 0
        ? resolveFromAnswers(helpAnswers).resourceCategories
        : resolveContactNow().resourceCategories;

  const categories =
    resourceCategories && resourceCategories.length > 0
      ? resourceCategories
      : fallbackCategories;

  const selection = selectResourcesForCategories(
    categories,
    qrContext?.resourceSetId,
  );

  function goHome() {
    if (fromHospitality) {
      clearHospitalityAnswers();
      setHospitalityBookmark(null);
    } else if (fromConcern) {
      clearConcernAnswers();
      setConcernBookmark(null);
    } else {
      clearHelpAnswers();
      setHelpBookmark(null);
    }
    setResourceCategories(null);
    clearQrContext();
    navigate("/");
  }

  function goBack() {
    setResourceCategories(null);
    navigate(backTo);
  }

  return (
    <section className="stack help-flow" aria-labelledby="resources-heading">
      <h1 id="resources-heading" tabIndex={-1}>
        {translate("page.resources.title")}
      </h1>

      {selection.pilotJurisdictionPending ? (
        <p className="draft-banner" role="status">
          {translate("resources.jurisdiction_pending")}
        </p>
      ) : null}

      <p className="muted">{translate("resources.verified_intro")}</p>
      <p className="muted">{translate("resources.external_privacy")}</p>

      <h2 className="mode-heading">{translate("resources.heading")}</h2>

      {selection.resources.length === 0 ? (
        <p className="muted">{translate("resources.empty")}</p>
      ) : (
        <ul className="resource-list">
          {selection.resources.map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              translate={translate}
            />
          ))}
        </ul>
      )}

      {selection.unavailableCategories.length > 0 ? (
        <div className="stack">
          <h2 className="mode-heading">
            {translate("resources.unavailable_heading")}
          </h2>
          <p className="muted">{translate("resources.unavailable_intro")}</p>
          <ul className="resource-list">
            {selection.unavailableCategories.map((categoryId) => {
              const labelKey =
                CATEGORY_LABEL_KEYS[categoryId] ??
                `resources.category.${categoryId}`;
              return (
                <li key={categoryId} className="resource-card resource-card--pending">
                  <h3>{translate(labelKey)}</h3>
                  <p className="muted">
                    {translate("resources.category_unavailable")}
                  </p>
                </li>
              );
            })}
          </ul>
          {selection.usedNationalFallback ? (
            <p className="muted">{translate("resources.national_fallback_note")}</p>
          ) : null}
        </div>
      ) : null}

      <FlowNav onBack={goBack} onHome={goHome} />
    </section>
  );
}
